import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Chip, CircularProgress, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import TerminalOutlinedIcon from '@mui/icons-material/TerminalOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import MyLocationOutlinedIcon from '@mui/icons-material/MyLocationOutlined';
import ReplayIcon from '@mui/icons-material/Replay';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { apiFetch } from '../config/apiClient';

const PANEL_FONT = '"IBM Plex Sans", "Aptos", "Segoe UI", system-ui, sans-serif';
const PANEL_MONO_FONT = '"IBM Plex Mono", "SFMono-Regular", Menlo, Consolas, monospace';

export type AgentEvalRequest = {
  country: string;
  clusterId: number;
  clusterName?: string;
  lon?: number;
  lat?: number;
  task?: string;
  taskLabel?: string;
};

// Mirrors the TASKS registry in backend/routers/agent_stream.py.
export const MORPH_TASKS: { id: string; label: string; blurb: string }[] = [
  { id: 'cluster_review', label: 'Drill Cluster Review', blurb: 'Data, land status, house-tier verdict' },
  { id: 'data_fetch', label: 'Data Fetch', blurb: 'Inventory local + survey data, list gaps and sources' },
  { id: 'competitive', label: 'Regional Competitive Analysis', blurb: 'Holders, applications and activity within 25 km' },
  { id: 'natura_community', label: 'Natura / Community Analysis', blurb: 'Environmental, heritage and community constraints' },
  { id: 'archive_search', label: 'Archive Deep Search', blurb: 'Hunt survey archives by claim no., coords and name variants' },
  { id: 'geochem_screen', label: 'Geochem Screen', blurb: 'Raw till/rock anomalies within 10 km, pitfall-checked' },
  { id: 'polygon_sketch', label: 'Staking Polygon Sketch', blurb: 'Concept polygon — extent, hectares, captures vs avoids' },
  { id: 'drill_summary', label: 'Drill Data Summary', blurb: 'Best intercepts in house format, source-verified' },
  { id: 'deposit_model', label: 'Deposit Model Match', blurb: 'Type hypothesis, district analogs, discriminating test' },
];

// Fixture name per task — cluster_review keeps the legacy un-suffixed key
// (matches replay_key() in backend/routers/agent_stream.py).
const replayKeyFor = (request: AgentEvalRequest): string => {
  const task = request.task || 'cluster_review';
  const suffix = task === 'cluster_review' ? '' : `-${task}`;
  return `${request.country}-${request.clusterId}${suffix}`;
};

type RunPhase = 'connecting' | 'running' | 'done' | 'error';

type FeedItem =
  | { kind: 'status'; text: string }
  | { kind: 'text'; text: string }
  | {
      kind: 'tool';
      tool: string;
      summary: string;
      input?: any;
      toolUseId?: string;
      sub?: boolean;
      resultSummary?: string;
      resultIsError?: boolean;
    };

type FinalResult = {
  text: string;
  isError: boolean;
  costUsd?: number;
  turns?: number;
  durationMs?: number;
};

const TOOL_ICONS: Record<string, React.ReactElement> = {
  Read: <DescriptionOutlinedIcon sx={{ fontSize: 13 }} />,
  NotebookRead: <DescriptionOutlinedIcon sx={{ fontSize: 13 }} />,
  Bash: <TerminalOutlinedIcon sx={{ fontSize: 13 }} />,
  Grep: <SearchOutlinedIcon sx={{ fontSize: 13 }} />,
  Glob: <SearchOutlinedIcon sx={{ fontSize: 13 }} />,
  WebFetch: <PublicOutlinedIcon sx={{ fontSize: 13 }} />,
  WebSearch: <PublicOutlinedIcon sx={{ fontSize: 13 }} />,
  Write: <EditOutlinedIcon sx={{ fontSize: 13 }} />,
  Edit: <EditOutlinedIcon sx={{ fontSize: 13 }} />,
  Task: <AccountTreeOutlinedIcon sx={{ fontSize: 13 }} />,
  Agent: <AccountTreeOutlinedIcon sx={{ fontSize: 13 }} />,
};

const toolIcon = (tool: string) => TOOL_ICONS[tool] || <BuildOutlinedIcon sx={{ fontSize: 13 }} />;

// "claude-fable-5[1m]" → "Fable 5", "claude-opus-4-8" → "Opus 4.8"
const prettyModel = (model?: string): string | undefined => {
  if (!model) return undefined;
  const parts = model.replace(/^claude-/, '').replace(/\[.*\]$/, '').split('-').filter(Boolean);
  if (parts.length === 0) return model;
  const name = parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
  const version = parts.slice(1).filter((p) => /^\d+$/.test(p)).join('.');
  return version ? `${name} ${version}` : name;
};

const formatToolInput = (input: any): string => {
  if (!input || typeof input !== 'object') return String(input ?? '');
  const keys = Object.keys(input);
  if (keys.length === 1 && typeof input[keys[0]] === 'string') return input[keys[0]];
  return JSON.stringify(input, null, 1);
};

const summarizeToolInput = (tool: string, input: any): string => {
  if (!input || typeof input !== 'object') return '';
  const candidate =
    input.file_path || input.path || input.command || input.pattern || input.query ||
    input.url || input.description || input.prompt ||
    Object.values(input).find((value) => typeof value === 'string' && value.length > 0);
  const text = typeof candidate === 'string' ? candidate.replace(/\s+/g, ' ').trim() : '';
  return text.length > 130 ? `${text.slice(0, 130)}…` : text;
};

const phaseChip = (phase: RunPhase, replaying: boolean): { label: string; color: string; bg: string } => {
  if (replaying && (phase === 'connecting' || phase === 'running')) {
    return { label: 'REPLAY', color: '#c4b5fd', bg: 'rgba(139, 92, 246, 0.16)' };
  }
  switch (phase) {
    case 'connecting':
      return { label: 'CONNECTING', color: '#fcd34d', bg: 'rgba(251, 191, 36, 0.14)' };
    case 'running':
      return { label: 'RUNNING', color: '#7dd3fc', bg: 'rgba(56, 189, 248, 0.14)' };
    case 'done':
      return { label: 'DONE', color: '#86efac', bg: 'rgba(74, 222, 128, 0.14)' };
    case 'error':
      return { label: 'ERROR', color: '#fca5a5', bg: 'rgba(248, 113, 113, 0.14)' };
  }
};

const AgentEvaluationPanel: React.FC<{
  request: AgentEvalRequest | null;
  onClose: () => void;
  onFlyTo?: (lon: number, lat: number) => void;
  embedded?: boolean;
}> = ({ request, onClose, onFlyTo, embedded = false }) => {
  const [phase, setPhase] = useState<RunPhase>('connecting');
  const [feed, setFeed] = useState<FeedItem[]>([]);
  const [collapsedCalls, setCollapsedCalls] = useState<Set<number>>(new Set());
  const [finalResult, setFinalResult] = useState<FinalResult | null>(null);
  const [allReplays, setAllReplays] = useState<string[]>([]);
  const [isReplaying, setIsReplaying] = useState(false);
  const [runNonce, setRunNonce] = useState(0);
  const [taskOverride, setTaskOverride] = useState<{ task: string; taskLabel: string } | null>(null);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const liveRequestedRef = useRef(false);
  const instantRequestedRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const transcriptEndRef = useRef<HTMLDivElement | null>(null);

  // Derived-state reset: a new request from the map clears any task override
  // chosen via the history chips (must happen during render, before the run
  // effect reads requestKey).
  const lastRequestRef = useRef(request);
  if (lastRequestRef.current !== request) {
    lastRequestRef.current = request;
    if (taskOverride) setTaskOverride(null);
  }

  const effectiveRequest = request ? { ...request, ...(taskOverride || {}) } : null;
  const requestKey = effectiveRequest ? replayKeyFor(effectiveRequest) : null;

  // This cluster's recorded runs, parsed from fixture names.
  const clusterRuns = React.useMemo(() => {
    if (!request) return [] as { fixture: string; task: string; label: string }[];
    const prefix = `${request.country}-${request.clusterId}`;
    return allReplays
      .filter((name) => name === prefix || name.startsWith(`${prefix}-`))
      .map((name) => {
        const task = name === prefix ? 'cluster_review' : name.slice(prefix.length + 1);
        const label = MORPH_TASKS.find((t) => t.id === task)?.label || task;
        return { fixture: name, task, label };
      });
  }, [allReplays, request]);

  const handleEvent = useCallback((event: any) => {
    switch (event.type) {
      case 'init': {
        setPhase('running');
        const model = prettyModel(event.model);
        setFeed((prev) => [...prev, { kind: 'status', text: `Morph session started${model ? ` · ${model}` : ''}` }]);
        break;
      }
      case 'status':
        if (event.replay) setIsReplaying(true);
        setFeed((prev) => [...prev, { kind: 'status', text: event.message || '' }]);
        break;
      case 'text': {
        const text: string = event.text || '';
        if (!text) break;
        setPhase('running');
        setFeed((prev) => {
          const last = prev[prev.length - 1];
          if (last && last.kind === 'text') {
            return [...prev.slice(0, -1), { kind: 'text', text: last.text + text }];
          }
          return [...prev, { kind: 'text', text }];
        });
        break;
      }
      case 'tool_use':
        setPhase('running');
        setFeed((prev) => [
          ...prev,
          {
            kind: 'tool',
            tool: event.tool || 'tool',
            summary: summarizeToolInput(event.tool, event.input),
            input: event.input,
            toolUseId: event.tool_use_id,
            sub: Boolean(event.sub),
          },
        ]);
        break;
      case 'tool_result':
        if (!event.tool_use_id) break;
        setFeed((prev) =>
          prev.map((item) =>
            item.kind === 'tool' && item.toolUseId === event.tool_use_id
              ? { ...item, resultSummary: event.summary || '', resultIsError: Boolean(event.is_error) }
              : item
          )
        );
        break;
      case 'result':
        setFinalResult({
          text: event.result || '',
          isError: Boolean(event.is_error),
          costUsd: typeof event.total_cost_usd === 'number' ? event.total_cost_usd : undefined,
          turns: typeof event.num_turns === 'number' ? event.num_turns : undefined,
          durationMs: typeof event.duration_ms === 'number' ? event.duration_ms : undefined,
        });
        setPhase(event.is_error ? 'error' : 'done');
        break;
      case 'error':
        setFeed((prev) => [...prev, { kind: 'status', text: `Error: ${event.message || 'unknown'}` }]);
        setPhase('error');
        break;
      default:
        break;
    }
  }, []);

  // Fetch recorded fixtures so the run-history chips only show real runs.
  useEffect(() => {
    if (!requestKey) return;
    let cancelled = false;
    apiFetch('/api/agent/replays')
      .then((res) => res.json())
      .then((data) => {
        if (!cancelled && Array.isArray(data?.replays)) setAllReplays(data.replays);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [requestKey, phase]);

  // Run the evaluation whenever the request or nonce changes. Default policy:
  // if a recorded run exists for this cluster/task, open it as a replay (demo
  // pacing, no cost, no wait) — "Re-run live" forces a real run.
  useEffect(() => {
    if (!request) return;
    const wantLive = liveRequestedRef.current;
    liveRequestedRef.current = false;
    const wantInstant = instantRequestedRef.current;
    instantRequestedRef.current = false;

    setPhase('connecting');
    setFeed([]);
    setCollapsedCalls(new Set());
    setFinalResult(null);
    setSaveState('idle');

    const controller = new AbortController();
    abortRef.current = controller;

    const active = { ...request, ...(taskOverride || {}) };
    const params = new URLSearchParams({
      country: active.country,
      cluster_id: String(active.clusterId),
      task: active.task || 'cluster_review',
    });
    if (active.clusterName) params.set('name', active.clusterName);
    if (typeof active.lon === 'number') params.set('lon', String(active.lon));
    if (typeof active.lat === 'number') params.set('lat', String(active.lat));

    (async () => {
      try {
        let replayName: string | null = null;
        if (!wantLive && requestKey) {
          try {
            const replaysResponse = await apiFetch('/api/agent/replays');
            const replaysData = await replaysResponse.json();
            if (Array.isArray(replaysData?.replays)) {
              setAllReplays(replaysData.replays);
              if (replaysData.replays.includes(requestKey)) replayName = requestKey;
            }
          } catch {
            // Replay lookup failing should never block a run — fall through to live.
          }
        }
        if (controller.signal.aborted) return;
        setIsReplaying(Boolean(replayName));
        if (replayName) {
          params.set('replay', replayName);
          if (wantInstant) params.set('replay_speed', '0');
        }
        const response = await apiFetch(`/api/agent/evaluate-cluster?${params.toString()}`, {
          signal: controller.signal,
          headers: { Accept: 'text/event-stream' },
        });
        if (!response.ok || !response.body) {
          throw new Error(`HTTP ${response.status}`);
        }
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const chunks = buffer.split('\n\n');
          buffer = chunks.pop() || '';
          for (const chunk of chunks) {
            for (const line of chunk.split('\n')) {
              if (!line.startsWith('data: ')) continue;
              try {
                handleEvent(JSON.parse(line.slice(6)));
              } catch {
                // Malformed event — skip rather than kill the stream.
              }
            }
          }
        }
        setPhase((current) => (current === 'running' || current === 'connecting' ? 'error' : current));
      } catch (error: any) {
        if (error?.name === 'AbortError') return;
        setFeed((prev) => [...prev, { kind: 'status', text: `Stream failed: ${error?.message || error}` }]);
        setPhase('error');
      }
    })();

    return () => { controller.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestKey, runNonce]);

  // Keep the feed pinned to the latest activity.
  useEffect(() => {
    if (embedded) {
      transcriptEndRef.current?.scrollIntoView({ block: 'end' });
      return;
    }
    const node = scrollRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [embedded, feed, finalResult]);

  const saveVerdict = async () => {
    if (!effectiveRequest || !finalResult || finalResult.isError || saveState === 'saving' || saveState === 'saved') return;
    setSaveState('saving');
    try {
      const response = await apiFetch('/api/agent/save-verdict', {
        method: 'POST',
        body: JSON.stringify({
          country: effectiveRequest.country,
          cluster_id: effectiveRequest.clusterId,
          name: effectiveRequest.clusterName,
          task: effectiveRequest.task || 'cluster_review',
          verdict_text: finalResult.text,
          total_cost_usd: finalResult.costUsd,
          num_turns: finalResult.turns,
          duration_ms: finalResult.durationMs,
          from_replay: isReplaying,
          lon: effectiveRequest.lon,
          lat: effectiveRequest.lat,
        }),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      setSaveState('saved');
    } catch {
      setSaveState('error');
    }
  };

  if (!request) return null;

  const chip = phaseChip(phase, isReplaying);
  const isActive = phase === 'connecting' || phase === 'running';

  // Explicit call numbering — "#3" on a card means the agent's third call.
  const callNumbers: Record<number, number> = {};
  let callCount = 0;
  feed.forEach((item, index) => {
    if (item.kind === 'tool') callNumbers[index] = ++callCount;
  });

  return (
    <Paper
      elevation={embedded ? 0 : 14}
      sx={{
        position: embedded ? 'relative' : 'absolute',
        top: embedded ? 'auto' : { xs: 76, md: 92 },
        left: embedded ? 'auto' : 16,
        width: embedded ? '100%' : 432,
        maxWidth: embedded ? 'none' : 'calc(100vw - 32px)',
        height: 'auto',
        minHeight: embedded ? 'min(72vh, 760px)' : 'auto',
        maxHeight: embedded ? 'none' : 'calc(100% - 116px)',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: embedded ? '#090e13' : 'rgba(5, 8, 13, 0.94)',
        color: '#e5e7eb',
        border: embedded ? '1px solid rgba(125, 211, 252, 0.20)' : '1px solid rgba(226, 232, 240, 0.30)',
        borderRadius: embedded ? 1.25 : 2,
        boxShadow: embedded ? 'inset 0 1px 0 rgba(255, 255, 255, 0.025)' : '0 22px 48px rgba(0, 0, 0, 0.48)',
        backdropFilter: embedded ? 'none' : 'blur(14px)',
        overflow: 'hidden',
        pointerEvents: 'auto',
        zIndex: embedded ? 'auto' : 1395,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.35, py: 1.1, borderBottom: '1px solid rgba(148, 163, 184, 0.22)' }}>
        <Box component="img" src="/geomorphic-icon-white.png" alt="" sx={{ height: 20, width: 'auto', display: 'block' }} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ color: '#f8fafc', fontFamily: PANEL_FONT, fontWeight: 800, fontSize: '13px', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            Morph — Agent Evaluation
          </Typography>
          <Typography sx={{ color: '#94a3b8', fontFamily: PANEL_FONT, fontSize: '9.5px', lineHeight: 1.25, mt: 0.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {request.clusterName || `Cluster ${request.clusterId}`} · {request.country} · {effectiveRequest?.taskLabel || MORPH_TASKS.find((t) => t.id === (effectiveRequest?.task || 'cluster_review'))?.label || 'Drill Cluster Review'}
          </Typography>
        </Box>
        <Chip
          label={chip.label}
          size="small"
          sx={{ height: 20, borderRadius: 1, backgroundColor: chip.bg, color: chip.color, fontFamily: PANEL_MONO_FONT, fontSize: '8px', fontWeight: 900 }}
        />
        {typeof request.lon === 'number' && typeof request.lat === 'number' && onFlyTo && (
          <Tooltip title="Fly to cluster">
            <IconButton size="small" onClick={() => onFlyTo(request.lon as number, request.lat as number)} sx={{ color: '#94a3b8' }}>
              <MyLocationOutlinedIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        )}
        <IconButton size="small" onClick={onClose} sx={{ color: '#94a3b8' }}>
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      <Box ref={scrollRef} sx={{ p: 1.25, overflowY: embedded ? 'visible' : 'auto', flex: 1, minHeight: 120 }}>
        {feed.length === 0 && isActive && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
            <CircularProgress size={14} sx={{ color: '#e2e8f0' }} />
            <Typography sx={{ color: '#94a3b8', fontFamily: PANEL_FONT, fontSize: '10.5px' }}>
              Launching Morph in the Geomorphic workspace…
            </Typography>
          </Box>
        )}

        {feed.map((item, index) => {
          if (item.kind === 'status') {
            return (
              <Typography key={index} sx={{ color: '#64748b', fontFamily: PANEL_MONO_FONT, fontSize: '8.5px', textTransform: 'uppercase', letterSpacing: '0.04em', my: 0.6 }}>
                {item.text}
              </Typography>
            );
          }
          if (item.kind === 'tool') {
            const expanded = !collapsedCalls.has(index);
            const inputDetail = formatToolInput(item.input);
            return (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.3,
                  borderRadius: 1,
                  border: '1px solid rgba(148, 163, 184, 0.18)',
                  backgroundColor: 'rgba(30, 41, 59, 0.45)',
                  px: 0.9,
                  py: 0.6,
                  my: 0.55,
                  ml: item.sub ? 1.5 : 0,
                }}
              >
                <Box
                  onClick={() =>
                    setCollapsedCalls((prev) => {
                      const next = new Set(prev);
                      if (next.has(index)) next.delete(index); else next.add(index);
                      return next;
                    })
                  }
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.7, color: '#7dd3fc', cursor: 'pointer' }}
                >
                  <Typography sx={{ color: '#64748b', fontFamily: PANEL_MONO_FONT, fontSize: '8px', fontWeight: 800, minWidth: 18 }}>
                    #{callNumbers[index]}
                  </Typography>
                  {toolIcon(item.tool)}
                  <Typography sx={{ color: '#7dd3fc', fontFamily: PANEL_MONO_FONT, fontSize: '9px', fontWeight: 800 }}>
                    {item.tool}{item.sub ? ' · subagent' : ''}
                  </Typography>
                  {!expanded && (
                    <Typography sx={{ color: '#cbd5e1', fontFamily: PANEL_MONO_FONT, fontSize: '9px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }} title={item.summary}>
                      {item.summary}
                    </Typography>
                  )}
                  <Box sx={{ flex: expanded ? 1 : 0 }} />
                  <ExpandMoreIcon sx={{ fontSize: 13, color: '#64748b', transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
                </Box>
                {expanded && inputDetail && (
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      px: 0.8,
                      py: 0.6,
                      borderRadius: 0.5,
                      backgroundColor: 'rgba(2, 6, 12, 0.65)',
                      color: '#cbd5e1',
                      fontFamily: PANEL_MONO_FONT,
                      fontSize: '8.5px',
                      lineHeight: 1.45,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      maxHeight: embedded ? 'none' : 170,
                      overflowY: embedded ? 'visible' : 'auto',
                    }}
                  >
                    {inputDetail}
                  </Box>
                )}
                {item.resultSummary !== undefined && (
                  <Typography
                    sx={{
                      color: item.resultIsError ? '#fca5a5' : '#94a3b8',
                      fontFamily: PANEL_MONO_FONT,
                      fontSize: '8.5px',
                      lineHeight: 1.35,
                      display: '-webkit-box',
                      WebkitLineClamp: expanded ? 'unset' : 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {item.resultIsError ? '✗ ' : '→ '}{item.resultSummary || '(empty result)'}
                  </Typography>
                )}
              </Box>
            );
          }
          return (
            <Typography key={index} sx={{ color: '#e2e8f0', fontFamily: PANEL_FONT, fontSize: '10.5px', lineHeight: 1.5, whiteSpace: 'pre-wrap', my: 0.5 }}>
              {item.text}
            </Typography>
          );
        })}

        {isActive && feed.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mt: 0.6 }}>
            <CircularProgress size={11} sx={{ color: '#e2e8f0' }} />
            <Typography sx={{ color: '#64748b', fontFamily: PANEL_MONO_FONT, fontSize: '8.5px' }}>working…</Typography>
          </Box>
        )}

        {finalResult && (
          <Box
            sx={{
              borderRadius: 1,
              border: `1px solid ${finalResult.isError ? 'rgba(248, 113, 113, 0.45)' : 'rgba(74, 222, 128, 0.40)'}`,
              backgroundColor: finalResult.isError ? 'rgba(248, 113, 113, 0.08)' : 'rgba(74, 222, 128, 0.07)',
              px: 1,
              py: 0.8,
              mt: 1,
            }}
          >
            <Typography sx={{ color: finalResult.isError ? '#fca5a5' : '#86efac', fontFamily: PANEL_MONO_FONT, fontSize: '8px', fontWeight: 900, textTransform: 'uppercase', mb: 0.4 }}>
              {finalResult.isError ? 'Run failed' : 'Verdict'}
            </Typography>
            <Typography sx={{ color: '#e5e7eb', fontFamily: PANEL_FONT, fontSize: '10.5px', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
              {finalResult.text || '(no result text)'}
            </Typography>
          </Box>
        )}
        <Box ref={transcriptEndRef} aria-hidden sx={{ height: 0 }} />
      </Box>

      {!isActive && clusterRuns.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.6, px: 1.25, py: 0.7, borderTop: '1px solid rgba(148, 163, 184, 0.18)' }}>
          <Typography sx={{ color: '#64748b', fontFamily: PANEL_MONO_FONT, fontSize: '8px', fontWeight: 800, textTransform: 'uppercase', mr: 0.4 }}>
            Recorded
          </Typography>
          {clusterRuns.map((run) => (
            <Chip
              key={run.fixture}
              icon={<ReplayIcon sx={{ fontSize: '11px !important', color: '#c4b5fd' }} />}
              label={run.label}
              size="small"
              onClick={() => {
                setTaskOverride({ task: run.task, taskLabel: run.label });
                setRunNonce((n) => n + 1);
              }}
              sx={{
                height: 20,
                borderRadius: 1,
                backgroundColor: 'rgba(139, 92, 246, 0.12)',
                border: '1px solid rgba(139, 92, 246, 0.30)',
                color: '#d8cffc',
                fontFamily: PANEL_MONO_FONT,
                fontSize: '8px',
                fontWeight: 800,
                cursor: 'pointer',
                '&:hover': { backgroundColor: 'rgba(139, 92, 246, 0.24)' },
              }}
            />
          ))}
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.25, py: 0.8, borderTop: '1px solid rgba(148, 163, 184, 0.22)' }}>
        <Typography sx={{ color: '#64748b', fontFamily: PANEL_MONO_FONT, fontSize: '8.5px', flex: 1 }}>
          {finalResult && !finalResult.isError
            ? [
                finalResult.costUsd !== undefined ? `$${finalResult.costUsd.toFixed(2)}` : null,
                finalResult.turns !== undefined ? `${finalResult.turns} turns` : null,
                finalResult.durationMs !== undefined ? `${Math.round(finalResult.durationMs / 1000)}s` : null,
              ].filter(Boolean).join(' · ')
            : isReplaying ? 'Recorded run' : ''}
        </Typography>
        {!isActive && finalResult && !finalResult.isError && (
          <Button
            size="small"
            disabled={saveState === 'saving' || saveState === 'saved'}
            onClick={saveVerdict}
            sx={{
              color: saveState === 'saved' ? '#86efac' : saveState === 'error' ? '#fca5a5' : '#86efac',
              fontFamily: PANEL_MONO_FONT,
              fontSize: '8.5px',
              fontWeight: 800,
              minWidth: 0,
              px: 0.8,
              '&.Mui-disabled': { color: saveState === 'saved' ? '#86efac' : '#475569' },
            }}
          >
            {saveState === 'saved' ? '✓ Saved to campaign' : saveState === 'saving' ? 'Saving…' : saveState === 'error' ? 'Save failed — retry' : 'Save verdict'}
          </Button>
        )}
        {isActive && isReplaying && (
          <Button
            size="small"
            onClick={() => { instantRequestedRef.current = true; setRunNonce((n) => n + 1); }}
            sx={{ color: '#c4b5fd', fontFamily: PANEL_MONO_FONT, fontSize: '8.5px', fontWeight: 800, minWidth: 0, px: 0.8 }}
          >
            Skip to verdict
          </Button>
        )}
        {!isActive && (
          <Button
            size="small"
            onClick={() => { liveRequestedRef.current = true; setRunNonce((n) => n + 1); }}
            sx={{ color: '#7dd3fc', fontFamily: PANEL_MONO_FONT, fontSize: '8.5px', fontWeight: 800, minWidth: 0, px: 0.8 }}
          >
            Re-run live
          </Button>
        )}
      </Box>
    </Paper>
  );
};

export default AgentEvaluationPanel;
